import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import VendorModal from 'react-modal';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { getAbSetting } from "@/helpers/abTesting.ts";

import { ReactComponent as CloseIcon } from '@/assets/icons/close.svg';
import { ReactComponent as RestartIcon } from '@/assets/icons/restart.svg';

import Button from '@/components/Button';

import { useMobile } from '@/features/bed-configurator/helpers/responsiveHooks';

import List from './components/List';
import Slider from './components/Slider';

import ResultScreen from './screens/ResultScreen';
import StartScreen from './screens/StartScreen';

import { ReactComponent as ArrowIcon } from "../../assets/img/icons/arrow.svg";

import modalStyles from '@/components/Modal/index.module.scss';
import startScreenStyles from './screens/StartScreen/index.module.scss';
import styles from './index.module.scss';

import { Data, PartnerData, Tendencies, UserData } from './types/Data';
import QuizContext, { useQuiz, useQuizData, DEFAULT_DATA } from './contexts/QuizContext';

const tendencies: Tendencies[] = [
    'Schulterschmerzen',
    'Hüftschmerzen',
    'Nackenschmerzen',
    'Rückenschmerzen (ganzer Rücken)',
    'Rückenschmerzen (unterer Rücken)',
    'Schwitzen in der Nacht',
    'Milbenallergien',
    'Keine der Optionen trifft zu 🙃',
];

type TypedOmit<T, K extends keyof T> = Omit<T, K>;

type QuestionTexts = Record<keyof TypedOmit<UserData, 'firmness' | 'score' | 'bmi'>, Record<'you' | 'partner', string>>;
const questionTexts: QuestionTexts = {
    height: {
        you: 'Deine Körpergröße',
        partner: 'Körpergröße von PartnerIn',
    },
    weight: {
        you: 'Dein Körpergewicht',
        partner: 'Körpergewicht von PartnerIn',
    },
    gender: {
        you: 'Dein biologisches Geschlecht',
        partner: 'Biologisches Geschlecht von PartnerIn',
    },
    sleepPosition: {
        you: 'In der Regel liegst Du',
        partner: 'In der Regel liegt Dein(e) PartnerIn',
    },
    body: {
        you: 'Deine Körperform beschreibst Du eher',
        partner: 'Eheste Körperform von PartnerIn',
    },
    tendencies: {
        you: 'Du neigst zu',
        partner: 'Dein(e) PartnerIn neigt zu',
    },
    tossAndTurn: {
        you: 'In der Nacht wälzt Du Dich',
        partner: 'In der Nacht wälzt Dein(e) PartnerIn sich',
    },
    ownFirmness: {
        you: 'Unabhängig von dem für Dich richtigen Härtegrad bevorzugst Du ein',
        partner: 'Unabhängig von dem für Dein(e) PartnerIn richtigen Härtegrad bevorzugt er/sie',
    }
}

type Screens = 'start' | 'questions' | 'result';
type Tabs = 'you' | 'partner';
type Values<T extends keyof UserData> = UserData[T][];

interface MattressFirmnessQuizProps {
    isOpen: boolean
    onRequestClose: () => void
    onUseRecommendations: (recommendations: Record<string, string>) => void // TODO: type first `string` for attributes
    doubleBed: boolean
    embed?: boolean
}

function Quiz ({
    isOpen,
    onRequestClose,
    onUseRecommendations,
    doubleBed,
    embed = false,
}: MattressFirmnessQuizProps) {

    const { setData } = useQuiz(),
        { quizData, setQuizData } = useQuizData();

    const isMobile = useMobile();

    const [isOpenComplete, setIsOpenComplete] = useState(false);

    function handleAfterOpen() {
      setIsOpenComplete(true);
      restart('start');
    }

    function handleClose() {
      setIsOpenComplete(false);
      onRequestClose();
      restart('start');
    }

    function restart(restartScreen: Screens) {

        setData((data) => {
            const updatedQuizData = { ...data.quizData };

            if (!doubleBed) {
                delete updatedQuizData.partner;
            }

            return {
                ...data,
                quizData: updatedQuizData,
                doubleBed,
            };
        });

        setScreen(restartScreen || screen);
    }

    const [screen, setScreen] = useState<Screens>('start');
    const [currentTab, setCurrentTab] = useState<Tabs>('you');

    const tabData = quizData[currentTab]!;
    function setTabData(userData: Partial<UserData | PartnerData>) {

        setQuizData(currentTab, {
            ...quizData[currentTab]!,
            ...userData,
        });
    }

    function getQuestionText (question: keyof QuestionTexts) {
        return questionTexts[question][currentTab];
    }

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {

        restart('start');
    }, []);

    useEffect(() => {

        if (!doubleBed && currentTab === 'partner') {
            setCurrentTab('you');
        }

        if (!embed && (!doubleBed && quizData.partner) || (doubleBed && !quizData.partner)) {
            restart('questions');
        }
    }, [quizData]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo(0, 0);
        }
    }, [screen, currentTab]);

    const isDoubleBedCurrentTab = doubleBed && currentTab === 'you';

    return (
        <VendorModal
            closeTimeoutMS={isMobile ? 700 : 200}
            className={classNames(modalStyles.wrapper, styles.modal, 'ab-mozart-mattressfirmness-quiz', `screen-wrapper-${screen}`)}
            overlayClassName={classNames(modalStyles.overlay, {
                [modalStyles.overlayComplete]: isOpenComplete,
            })}
            isOpen={isOpen}
            onRequestClose={handleClose}
            onAfterOpen={handleAfterOpen}
        >
            <div className={classNames(styles.modalContent, `screen-${screen}`)} ref={contentRef}>
                {screen === 'result' && (
                    <span
                        onClick={() => restart('questions')}
                        className={styles.restart}
                    >
                        Angaben ändern <RestartIcon />
                    </span>
                )}
               <CloseIcon className={styles.modalClose} onClick={handleClose} />

                <SwitchTransition>
                    <CSSTransition timeout={250} key={screen} classNames='slide'>
                        <div className={styles.quiz}>
                            {screen === 'start' && <StartScreen changeScreen={setScreen} />}

                            {screen === 'questions' && (
                                <div className='questions'>
                                    {!embed && (
                                        <Header
                                            doubleBed={doubleBed}
                                            selectedTab={currentTab}
                                            onChangeTab={setCurrentTab}
                                        />
                                    )}

                                    <div className={styles.content}>

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('height')}: <span className={styles.value}>{tabData.height}cm</span></h3>

                                            <Slider
                                                min={120} max={220}
                                                suffix='cm'
                                                value={tabData.height}
                                                onChange={(value) => setTabData({ height: value })}
                                            />
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('weight')}: <span className={styles.value}>{tabData.weight}kg</span></h3>

                                            <Slider
                                                min={40} max={150}
                                                suffix='kg'
                                                value={tabData.weight}
                                                onChange={(value) => setTabData({ weight: value})}
                                            />
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('gender')}:</h3>

                                            <div className={styles.list}>
                                                <List
                                                    values={[
                                                        'Mann',
                                                        'Frau',
                                                        'k.A.'
                                                    ] as Values<'gender'>}
                                                    onClick={(value) => setTabData({ gender: value })}
                                                    checkFn={(value) => tabData.gender === value}
                                                />
                                            </div>
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('sleepPosition')}:</h3>

                                            <div className={styles.list}>
                                                <List
                                                    perRow={isMobile ? 2 : 4}
                                                    values={[
                                                        'auf der Seite',
                                                        'auf dem Bauch',
                                                        'auf dem Rücken',
                                                        'häufig wechselnd',
                                                        'Weiß ich nicht genau'
                                                    ] as Values<'sleepPosition'>}
                                                    onClick={(value) => setTabData({ sleepPosition: value })}
                                                    checkFn={(value) => tabData.sleepPosition === value}
                                                />
                                            </div>
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('body')}:</h3>

                                            <List
                                                perRow={isMobile ? 2 : 4}
                                                values={[
                                                    'normal',
                                                    'schlank',
                                                    'kurvig',
                                                    'weiß nicht genau'
                                                ] as Values<'body'>}
                                                onClick={(value) => setTabData({ body: value})}
                                                checkFn={(value) => tabData.body === value}
                                            />
                                        </div>

                                        <hr />

                                        <div className={classNames(styles.question, styles.tendencyQuestion)}>
                                            <h3>{getQuestionText('tendencies')}:</h3>

                                            {tendencies.map((tendency) => {
                                                const id = tendency.toLowerCase().replace(/\s+/g, '-');
                                                return (
                                                    <div key={tendency} className={styles.tendency}>
                                                        <label id={id} className={styles.checkboxLabel}>
                                                            <input hidden id={id} type="checkbox" name={id}
                                                                checked={tabData.tendencies.includes(tendency)}
                                                                onChange={(event) => {
                                                                    if (event.target.checked) {
                                                                        if (tendency.startsWith('Keine der Optionen trifft zu')) {
                                                                            setTabData({ tendencies: [tendency] });
                                                                            return;
                                                                        }

                                                                        setTabData({
                                                                            tendencies: [...tabData.tendencies, tendency]
                                                                                .filter((t) => !t.startsWith('Keine der Optionen trifft zu'))
                                                                                .sort((a, b) => tendencies.indexOf(a) - tendencies.indexOf(b))
                                                                        });
                                                                        return;
                                                                    }

                                                                    setTabData({ tendencies: tabData.tendencies.filter((t) => t !== tendency) });
                                                                }} />
                                                            <span className={styles.checkbox}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                                    <path d="M5 12l5 5l10 -10"></path>
                                                                </svg>
                                                            </span>
                                                            <span>{tendency}</span>
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('tossAndTurn')}:</h3>

                                            <List
                                                perRow={isMobile ? 2 : 4}
                                                values={[
                                                    'manchmal',
                                                    'eher selten',
                                                    'eher oft',
                                                    'weiß nicht genau',
                                                ] as Values<'tossAndTurn'>}
                                                checkFn={(value) => tabData.tossAndTurn === value}
                                                onClick={(value) => setTabData({ tossAndTurn: value})}
                                            />
                                        </div>

                                        <hr />

                                        <div className={styles.question}>
                                            <h3>{getQuestionText('ownFirmness')}:</h3>

                                            <List
                                                perRow={2}
                                                values={[
                                                    'eher weiches Liegegefühl',
                                                    'eher festes Liegegefühl',
                                                    'keine Präferenz / Weiß nicht',
                                                ] as Values<'ownFirmness'>}
                                                checkFn={(value) => tabData.ownFirmness === value}
                                                onClick={(value) => setTabData({ ownFirmness: value})}
                                            />
                                        </div>

                                        <div className={styles.buttons}>
                                            {isDoubleBedCurrentTab ? (
                                                <>
                                                    <Button
                                                        className={classNames(startScreenStyles.button, styles.resultButton)}
                                                        onClick={() => setCurrentTab('partner')}
                                                        abClassName="Weiter mit PartnerIn"
                                                    >
                                                        Weiter mit PartnerIn <ArrowIcon />
                                                    </Button>

                                                    {currentTab === 'you' && (
                                                        <Button
                                                            className={classNames(startScreenStyles.button, styles.applyButton)}
                                                            onClick={() => {
                                                                setQuizData('partner', quizData.you);
                                                                setScreen('result');
                                                            }}
                                                            abClassName="Meine Empfehlung für die Partnerseite übernehmen"
                                                        >
                                                            <span>Meine Empfehlung für die Partnerseite übernehmen</span> <ArrowIcon />
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    className={classNames(startScreenStyles.button, styles.resultButton)}
                                                    onClick={() => setScreen('result')}
                                                    abClassName="Ergebnis anzeigen"
                                                >
                                                    Ergebnis anzeigen <ArrowIcon />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {screen === 'result' && (
                                <ResultScreen
                                    closeModal={handleClose}
                                    onUseRecommendations={onUseRecommendations}
                                    embed={embed}
                                />
                            )}
                        </div>
                    </CSSTransition>
                </SwitchTransition>
            </div>

            {/*

                    */}
        </VendorModal>
    );
}

interface HeaderProps {
    doubleBed: boolean
    onChangeTab: (tab: 'you' | 'partner') => void
    selectedTab: 'you' | 'partner'
}

function Header ({
    doubleBed,
    onChangeTab,
    selectedTab,
}: HeaderProps) {

    return (
        <div className={styles.header}>
            <div className={styles.tabs}>
                <div
                    className={classNames(styles.tab, { [styles.active]: selectedTab === 'you' })}
                    onClick={() => onChangeTab('you')}
                >
                    <span>Du</span>
                </div>
                {doubleBed && (
                    <div
                        className={classNames(styles.tab, { [styles.active]: selectedTab === 'partner' })}
                        onClick={() => onChangeTab('partner')}
                    >
                        <span>PartnerIn</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function setPartnerData (gender: UserData['gender'], partner: Data['partner']): PartnerData | undefined {
    if (!partner) {
        return undefined;
    }

    if (partner.changed) {
        return partner;
    }

    switch (gender) {
        case 'Mann':
            return {
                ...partner,
                height: 170,
                weight: 65,
                gender: 'Frau',
            }

        case 'Frau':
        case 'k.A.':
            return {
                ...partner,
                height: 180,
                weight: 85,
                gender: 'Mann',
            }
    }

    return partner;
}

export default function MattressFirmnessQuiz(props: MattressFirmnessQuizProps) {

    return (
        <QuizContext>
            <Quiz {...props} />
        </QuizContext>
    )
}
